import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
} from 'react-native';
import { Svg, Path } from 'react-native-svg';

// Arrow dropdown icon
const ArrowDropDownIcon = ({ color = '#005CBC' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M4.66667 6.66675L8 10.0001L11.3333 6.66675H4.66667Z" fill={color} />
  </Svg>
);

export interface RangeFilterValue {
  min?: number;
  max?: number;
}

interface DLORangeFilterProps {
  label: string;
  value?: RangeFilterValue | null;
  onApply: (value: RangeFilterValue | null) => void;
  style?: any;
}

const DLORangeFilter: React.FC<DLORangeFilterProps> = ({ 
  label, 
  value, 
  onApply, 
  style 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>({ 
    top: 0, 
    left: 0, 
    width: 0 
  });
  const [tempMin, setTempMin] = useState<string>(value?.min?.toString() || '');
  const [tempMax, setTempMax] = useState<string>(value?.max?.toString() || '');
  const buttonRef = useRef<any>(null);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      buttonRef.current.measure((x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        setPosition({
          top: pageY + height + 4,
          left: pageX,
          width: width,
        });
        setIsOpen(true);
      });
    } else {
      setIsOpen(false);
    }
  };

  const handleApply = () => {
    const minValue = tempMin ? parseFloat(tempMin) : undefined;
    const maxValue = tempMax ? parseFloat(tempMax) : undefined;
    
    const rangeValue = (minValue === undefined && maxValue === undefined) 
      ? null 
      : { min: minValue, max: maxValue };
    
    console.log(`${label} range filter applying:`, rangeValue);
    onApply(rangeValue);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempMin('');
    setTempMax('');
    onApply(null);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (value?.min !== undefined && value?.max !== undefined) {
      return `${value.min} - ${value.max}`;
    } else if (value?.min !== undefined) {
      return `≥ ${value.min}`;
    } else if (value?.max !== undefined) {
      return `≤ ${value.max}`;
    }
    return label;
  };

  const hasValue = value && (value.min !== undefined || value.max !== undefined);

  return (
    <>
      <TouchableOpacity
        ref={buttonRef}
        style={[styles.filterButton, hasValue && styles.filterButtonSelected, style]}
        onPress={handleToggle}
        activeOpacity={0.7}
      >
        <Text style={[styles.filterButtonText, hasValue && styles.filterButtonTextSelected]}>
          {getDisplayText()}
        </Text>
        <ArrowDropDownIcon />
      </TouchableOpacity>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <TouchableOpacity
          style={styles.overlay}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
        >
          <View
            style={[
              styles.dropdown,
              {
                top: position.top,
                left: position.left,
                minWidth: Math.max(position.width, 250),
              },
            ]}
          >
            <TouchableOpacity activeOpacity={1}>
              <View style={styles.dropdownContent}>
                <Text style={styles.dropdownTitle}>{label} Range</Text>
                
                <View style={styles.inputRow}>
                  <View style={styles.inputContainer}>
                    <Text style={styles.inputLabel}>Min</Text>
                    <TextInput
                      style={styles.input}
                      value={tempMin}
                      onChangeText={setTempMin}
                      placeholder="Min value"
                      keyboardType="numeric"
                      placeholderTextColor="#A0A0A0"
                    />
                  </View>
                  
                  <View style={styles.inputContainer}>
                    <Text style={styles.inputLabel}>Max</Text>
                    <TextInput
                      style={styles.input}
                      value={tempMax}
                      onChangeText={setTempMax}
                      placeholder="Max value"
                      keyboardType="numeric"
                      placeholderTextColor="#A0A0A0"
                    />
                  </View>
                </View>

                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={styles.clearButton}
                    onPress={handleClear}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.clearButtonText}>Clear</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={styles.applyButton}
                    onPress={handleApply}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.applyButtonText}>Apply</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  filterButton: {
    flexDirection: 'row',
    height: 32,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
  },
  filterButtonSelected: {
    backgroundColor: '#E8F2FF',
    borderColor: '#B3D9FF',
  },
  filterButtonText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  filterButtonTextSelected: {
    fontWeight: '600',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 1000,
  },
  dropdownContent: {
    padding: 16,
  },
  dropdownTitle: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 24,
    marginBottom: 16,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  inputContainer: {
    flex: 1,
  },
  inputLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
    marginBottom: 4,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    borderRadius: 6,
    paddingHorizontal: 12,
    backgroundColor: '#FBFCFE',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
  },
  clearButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
  },
  clearButtonText: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  applyButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 24,
    backgroundColor: '#005CBC',
  },
  applyButtonText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
});

export default DLORangeFilter;